import { Injectable } from "@angular/core";

import moment from "moment";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ListColumn } from "../../core/list/list-column.model";
import { DataGridService } from "../shared/data-grid/data-grid.service.interface";
import { AssetSummaryDTO } from "../shared/dto-models/assetSummaryDTO.model";
import { DropDownListDTO } from "../shared/dto-models/dropDownListDTO.model";
import { ExtendedDropDownListDTO } from "../shared/dto-models/extendedDropDownListDTO.model";
import { AssetCommand } from "../shared/models/assetCommand";
import { IDataGrid } from "../shared/models/dataGrid.model";
import { AuthService } from "../shared/services/auth.service";
import { ConfigurationService } from "../shared/services/configuration.service";
import { DataService } from "../shared/services/data.service";

@Injectable()
export class AssetService implements DataGridService<AssetSummaryDTO> {
  private ePermitsUrl = " ";

  constructor(
    private service: DataService,
    private configurationService: ConfigurationService,
    private authService: AuthService,
  ) {
    if (this.configurationService.isReady) {
      this.ePermitsUrl = this.configurationService.serverSettings.ePermitsUrl;
    } else {
      this.configurationService.settingsLoaded$.subscribe(
        (x) =>
          (this.ePermitsUrl =
            this.configurationService.serverSettings.ePermitsUrl),
      );
    }
  }

  copyRow(id: number): Observable<number> {
    return 0 as any;
  }

  getData(
    pageSize?: number,
    startRow?: number,
    sortActive?: string,
    sortDirection?: string,
    filters?: ListColumn[],
  ): Observable<IDataGrid<AssetSummaryDTO>> {
    let url = `${this.ePermitsUrl}/api/v1/asset/assets?pageSize=${pageSize}&pageIndex=${startRow}&sortActive=${sortActive}&sortDirection=${sortDirection}`;

    filters.forEach((filter) => {
      if (filter.filterValue || filter.filterValue === 0) {
        if (filter.filterValue instanceof Array) {
          url = `${url}&${filter.property}=${moment(
            filter.filterValue[0],
          ).format("YYYY-MM-DD HH:mm:ss")}|${moment(
            filter.filterValue[1],
          ).format("YYYY-MM-DD HH:mm:ss")}`;
        } else {
          url = `${url}&${filter.property}=${encodeURIComponent(
            filter.filterValue,
          )}`;
        }
      }
    });

    const visibleColumns = filters.map((obj) => {
      if (obj.visible) {
        return obj.property;
      }
    });

    url = `${url}&visibleColumns=${visibleColumns}`;

    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  changeStatus(command: any): Observable<true> {
    const url = `${this.ePermitsUrl}/api/v1/asset/changestatus`;

    return this.service.postWithId(url, command).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getDataById(id: any): any {
    const url = `${this.ePermitsUrl}/api/v1/asset/${id}`;

    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  createAsset(saveAsset: AssetCommand): any {
    const url = `${this.ePermitsUrl}/api/v1/asset/create`;

    return this.service.postWithId(url, saveAsset, null, true).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  updateAsset(saveAsset: AssetCommand): any {
    const url = `${this.ePermitsUrl}/api/v1/asset/update`;

    return this.service.postWithId(url, saveAsset, null, true).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getDropDownList(resource: string): Observable<DropDownListDTO[]> {
    const url = `${this.ePermitsUrl}/api/v1/asset/${resource}`;
    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getApiResource(
    resource: string,
    searchString: string,
    parentId: number,
    additionalFilterId?: number,
  ): Observable<ExtendedDropDownListDTO[]> {
    const url = `${this.ePermitsUrl}/api/v1/asset/${resource}?searchString=${searchString}&parentId=${parentId}&additionalFilterId=${additionalFilterId}`;
    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getApiResourceForBuilding(
    resource: string,
    searchString: string,
    buildingId: number,
    zoneId: number,
  ): Observable<DropDownListDTO[]> {
    const url = `${this.ePermitsUrl}/api/v1/asset/${resource}?searchString=${searchString}&buildingId=${buildingId}&zoneId=${zoneId}`;
    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getLocations(
    searchString: string,
    buildingId: number,
    zoneId: number,
    locationTypeId: number,
  ): any {
    const url = `${this.ePermitsUrl}/api/v1/asset/locations?buildingId=${buildingId}&searchString=${searchString}&zoneId=${zoneId}&locationTypeId=${locationTypeId}`;
    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  previewImage(documentId: number): any {
    const url = `${this.ePermitsUrl}/api/v1/asset/preview?documentId=${documentId}`;

    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  removeImage(documentId: number): any {
    const url = `${this.ePermitsUrl}/api/v1/asset/removeImage?documentId=${documentId}`;

    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  exportCsv(
    pageSize?: number,
    startRow?: number,
    sortActive?: string,
    sortDirection?: string,
    filters?: ListColumn[],
  ): Observable<any> {
    let url = `${this.ePermitsUrl}/api/v1/asset/export?pageSize=${pageSize}&pageIndex=${startRow}&sortActive=${sortActive}&sortDirection=${sortDirection}`;

    filters.forEach((filter) => {
      if (filter.filterValue) {
        url = `${url}&${filter.property}=${encodeURIComponent(
          filter.filterValue,
        )}`;
      }
    });

    return this.service.get(url).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  private isObject(value) {
    return value && typeof value === "object" && value.constructor === Object;
  }

  hasGotChildren(id: any): Observable<boolean> {
    const url = `${this.ePermitsUrl}/api/v1/asset/hasGotChildren?structureId=${id}`;
    return this.service.get(url).pipe(
      map((response: boolean) => {
        return response;
      }),
    );
  }
}
